import { useCallback, useEffect, useState } from 'react';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ChevronDown, Clapperboard, Clock, Code2, Eraser, Eye, FileCode2, FileText, Gauge, GripVertical, HelpCircle,
  Image as ImageIcon, Link2, ListChecks, MonitorPlay, Pencil, Plus, Trash2, X, Youtube,
} from 'lucide-react';
import { toast } from 'sonner';
import { coursesAPI } from '../lib/api';
import { t } from '../lib/i18n';
import { EmptyState, Field, Modal, btnGhost, btnPrimary, inputCls, panelCls } from './ui';
import { IconButton, RowMenu, adminBtnPrimary } from './adminUi';
import HtmlFrame from './HtmlFrame';
import LessonCheckEditor, { checkFromLesson } from './LessonCheckEditor';

export const LESSON_TYPES = [
  { key: 'video', label: 'Video (file or direct URL)', icon: Clapperboard },
  { key: 'youtube', label: 'YouTube', icon: Youtube },
  { key: 'vimeo', label: 'Vimeo', icon: MonitorPlay },
  { key: 'url', label: 'External URL', icon: Link2 },
  { key: 'iframe', label: 'Embedded page (iframe)', icon: MonitorPlay },
  { key: 'document', label: 'Document', icon: FileText },
  { key: 'image', label: 'Image', icon: ImageIcon },
  { key: 'html', label: 'HTML page', icon: Code2 },
  { key: 'quiz', label: 'Quiz', icon: HelpCircle },
  { key: 'wealth_scorecard', label: 'Wealth Baseline Scorecard', icon: Gauge },
];
const TYPE_META = Object.fromEntries(LESSON_TYPES.map((x) => [x.key, x]));
const URL_TYPES = ['video', 'youtube', 'vimeo', 'url', 'iframe'];

// s137: a lesson whose main material was cleared (or never added) — the row
// shows an "Empty" pill and the player a "no content yet" placeholder.
export function lessonIsEmpty(l) {
  const type = l?.lesson_type;
  if (URL_TYPES.includes(type)) return !(l.video && l.video.url);
  if (type === 'document' || type === 'image') return !(l.attachment && l.attachment.path);
  if (type === 'html') return !l.html;
  if (type === 'quiz') return !(l.quiz && l.quiz.questions && l.quiz.questions.length);
  return false;
}
const CONTENT_LABEL = {
  video: 'The video (URL or uploaded file)', youtube: 'The YouTube link', vimeo: 'The Vimeo link',
  url: 'The external link', iframe: 'The embedded page URL', document: 'The document file',
  image: 'The image file', html: 'The HTML page', quiz: 'All quiz questions',
};

// ---------------------------------------------------------------- quiz builder

function SortableQuestion({ q, index, onChange, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: q.id });
  const setOption = (i, value) => {
    const options = [...q.options];
    options[i] = value;
    onChange({ ...q, options });
  };
  const toggleCorrect = (i) => {
    const correct = q.correct.includes(i) ? q.correct.filter((x) => x !== i) : [...q.correct, i].sort();
    onChange({ ...q, correct });
  };
  const removeOption = (i) => {
    onChange({
      ...q,
      options: q.options.filter((_, j) => j !== i),
      correct: q.correct.filter((x) => x !== i).map((x) => (x > i ? x - 1 : x)),
    });
  };
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 }}
      className="rounded-md border border-lms-border bg-lms-raised/50 p-3"
    >
      <div className="mb-2 flex items-center gap-2">
        <button type="button" {...attributes} {...listeners} className="cursor-grab text-lms-faint hover:text-lms-muted" aria-label={t('Drag to reorder')}>
          <GripVertical size={14} />
        </button>
        <span className="text-xs text-lms-faint lms-num">Q{index + 1}</span>
        <input
          value={q.title}
          onChange={(e) => onChange({ ...q, title: e.target.value })}
          placeholder={t('Question')}
          className={`${inputCls} flex-1`}
        />
        <button type="button" onClick={onRemove} className="text-lms-muted hover:text-[var(--lms-danger)]" title={t('Delete')}>
          <Trash2 size={14} />
        </button>
      </div>
      <div className="space-y-1.5 pl-6">
        {q.options.map((opt, i) => (
          <div key={i} className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={q.correct.includes(i)}
              onChange={() => toggleCorrect(i)}
              title={t('Correct answer')}
            />
            <input
              value={opt}
              onChange={(e) => setOption(i, e.target.value)}
              placeholder={`${t('Option')} ${i + 1}`}
              className={`${inputCls} flex-1`}
            />
            <button type="button" onClick={() => removeOption(i)} className="text-lms-faint hover:text-[var(--lms-danger)]" aria-label={t('Remove')}>
              <X size={13} />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => onChange({ ...q, options: [...q.options, ''] })}
          className="text-xs text-lms-accent hover:brightness-110"
        >
          + {t('Add option')}
        </button>
      </div>
    </div>
  );
}

function QuizBuilder({ questions, onChange }) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const newQuestion = () => onChange([
    ...questions,
    { id: Math.random().toString(36).slice(2, 14), title: '', options: ['', ''], correct: [] },
  ]);
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const ids = questions.map((q) => q.id);
    onChange(arrayMove(questions, ids.indexOf(active.id), ids.indexOf(over.id)));
  };
  return (
    <div className="space-y-2">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={questions.map((q) => q.id)} strategy={verticalListSortingStrategy}>
          {questions.map((q, i) => (
            <SortableQuestion
              key={q.id}
              q={q}
              index={i}
              onChange={(next) => onChange(questions.map((x) => (x.id === q.id ? next : x)))}
              onRemove={() => onChange(questions.filter((x) => x.id !== q.id))}
            />
          ))}
        </SortableContext>
      </DndContext>
      <button type="button" onClick={newQuestion} className={btnGhost}>
        <Plus size={14} />
        {t('Add question')}
      </button>
      <p className="text-xs text-lms-faint">{t('Tick the checkbox beside each correct option (one or more).')}</p>
    </div>
  );
}

// ---------------------------------------------------------------- lesson modal

const HTML_MAX = 5 * 1024 * 1024;
const fmtKb = (n) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const utf8Len = (str) => new Blob([str || '']).size;

function HtmlSource({ html, setHtml, loading, onRemove }) {
  const [expanded, setExpanded] = useState(false);
  const pick = (file) => {
    if (!file) return;
    if (!/\.html?$/i.test(file.name)) { toast.error(t('Choose an .html file')); return; }
    if (file.size > HTML_MAX) { toast.error(t('The HTML file is larger than 5 MB')); return; }
    const reader = new FileReader();
    reader.onload = () => setHtml({ ...html, source: 'upload', filename: file.name, size: file.size, content: String(reader.result || ''), dirty: true });
    reader.readAsText(file);
  };
  const segBtn = (key, label) => (
    <button
      type="button"
      role="radio"
      aria-checked={html.source === key}
      onClick={() => setHtml({ ...html, source: key })}
      className={`h-9 rounded-lg px-4 text-[13.5px] ${html.source === key ? 'bg-lms-surface font-bold text-lms-ink shadow-[var(--lms-shadow-sm)]' : 'font-semibold text-lms-sub hover:text-lms-ink'}`}
    >
      {label}
    </button>
  );
  const heightCard = (key, title, sub) => (
    <label className={`flex flex-1 cursor-pointer items-start gap-3 rounded-xl border px-4 py-3.5 ${html.height === key ? 'border-lms-accent bg-lms-tag-free-bg/40' : 'border-lms-line'}`}>
      <input type="radio" name="html-height" checked={html.height === key} onChange={() => setHtml({ ...html, height: key })} className="mt-1 accent-[var(--lms-link)]" />
      <span className="flex flex-col gap-0.5">
        <span className="text-sm font-semibold text-lms-ink">{title}</span>
        <span className="text-[12.5px] text-lms-sub">{sub}</span>
      </span>
    </label>
  );
  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-2.5">
        <span className="text-[13px] font-semibold text-lms-body">{t('Source')}</span>
        <div role="radiogroup" aria-label={t('Source')} className="inline-flex self-start gap-1 rounded-[10px] bg-lms-raised p-1">
          {segBtn('upload', t('Upload .html file'))}
          {segBtn('paste', t('Paste code'))}
        </div>
        {html.source === 'upload' ? (
          <div className="flex items-center gap-3.5 rounded-xl border border-lms-line px-4 py-3.5">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[10px] bg-lms-tag-free-bg text-lms-link"><FileCode2 size={20} /></span>
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="truncate text-sm font-semibold text-lms-ink">{html.filename || (html.content ? t('Pasted HTML') : t('No file yet'))}</span>
              <span className="text-[12.5px] text-lms-sub">{loading ? t('Loading…') : html.content ? fmtKb(html.size || utf8Len(html.content)) : t('One self-contained .html file, up to 5 MB')}</span>
            </div>
            {html.content && onRemove && (
              <button type="button" onClick={onRemove} className="lms-press inline-flex h-10 shrink-0 items-center gap-1.5 rounded-lg px-3 text-[13.5px] font-bold text-lms-sale hover:bg-lms-raised">
                <Eraser size={14} /> {t('Remove')}
              </button>
            )}
            <label className="lms-press inline-flex h-10 shrink-0 cursor-pointer items-center rounded-lg border border-lms-ink px-3.5 text-[13.5px] font-bold text-lms-ink hover:bg-lms-raised">
              {html.content ? t('Replace') : t('Upload')}
              <input type="file" accept=".html,.htm,text/html" className="hidden" onChange={(e) => { pick(e.target.files[0]); e.target.value = ''; }} />
            </label>
          </div>
        ) : (
          <textarea
            value={html.content || ''}
            onChange={(e) => setHtml({ ...html, content: e.target.value, size: utf8Len(e.target.value), filename: '', dirty: true })}
            rows={9}
            spellCheck={false}
            placeholder="<!doctype html>…"
            aria-label={t('HTML code')}
            className={`${inputCls} font-mono text-[12.5px] leading-relaxed`}
          />
        )}
        <span className="text-[12.5px] leading-relaxed text-lms-sub">
          {t('Images and fonts must use full https:// links. Scripts run isolated — they can’t read the student’s session.')}
        </span>
      </div>

      {html.content && (
        <div className="flex flex-col gap-2.5">
          <div className="flex items-baseline justify-between">
            <span className="text-[13px] font-semibold text-lms-body">{t('Preview')}</span>
            <button type="button" onClick={() => setExpanded(!expanded)} className="text-[13px] font-bold text-lms-link hover:underline">
              {expanded ? t('Collapse preview') : t('Open full size')}
            </button>
          </div>
          <HtmlFrame
            html={html.content}
            title={t('Preview')}
            height={expanded ? 'auto' : 'fixed'}
            fixedPx={260}
            className="overflow-hidden rounded-xl border border-lms-line"
          />
        </div>
      )}

      <div className="flex flex-col gap-2.5">
        <span className="text-[13px] font-semibold text-lms-body">{t('Height on the lesson page')}</span>
        <div className="flex flex-col gap-3 sm:flex-row">
          {heightCard('auto', t('Fit to content'), t('Grows with the page, no inner scroll. Recommended.'))}
          {heightCard('fixed', t('Fixed height'), t('For apps or slides that scroll inside themselves.'))}
        </div>
        {html.height === 'fixed' && (
          <label className="flex items-center gap-3 text-sm text-lms-body">
            {t('Height')}
            <input
              type="number"
              min={300}
              max={4000}
              step={20}
              value={html.fixed_px}
              onChange={(e) => setHtml({ ...html, fixed_px: Number(e.target.value) || 720 })}
              className={`${inputCls} w-28`}
            />
            px
          </label>
        )}
      </div>
    </div>
  );
}

function LessonModal({ courseId, sectionId, lesson, onClose, onSaved }) {
  const editing = Boolean(lesson?.id);
  const [tab, setTab] = useState('content');
  const [title, setTitle] = useState(lesson?.title || '');
  const [type, setType] = useState(lesson?.lesson_type || 'video');
  const [url, setUrl] = useState(lesson?.video?.url || '');
  const [duration, setDuration] = useState(lesson?.video?.duration || '');
  const [attachment, setAttachment] = useState(lesson?.attachment || { path: '', type: '' });
  const [summary, setSummary] = useState(lesson?.summary || '');
  const [questions, setQuestions] = useState(lesson?.quiz?.questions?.map((q) => ({
    id: q.id, title: q.title, options: [...q.options], correct: [...(q.correct || [])],
  })) || []);
  const [check, setCheck] = useState(() => checkFromLesson(lesson));
  const [html, setHtml] = useState(() => ({
    source: lesson?.html?.source || 'upload',
    filename: lesson?.html?.filename || '',
    size: lesson?.html?.size || 0,
    height: lesson?.html?.height || 'auto',
    fixed_px: lesson?.html?.fixed_px || 720,
    content: '',
    dirty: false,
  }));
  const [htmlLoading, setHtmlLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [probing, setProbing] = useState(false);
  const [uploading, setUploading] = useState(false);

  const isUrlType = URL_TYPES.includes(type);
  const isQuiz = type === 'quiz';
  const isScorecard = type === 'wealth_scorecard';
  const isHtml = type === 'html';
  // The check applies to every lesson except the ones that grade themselves.
  const checkable = !isQuiz && !isScorecard;
  const checkCount = check.questions.filter((q) => q.q.trim()).length;

  // Editing an HTML lesson: pull its stored source for the preview / paste box.
  const lessonId = lesson?.id;
  const storedHtml = lesson?.lesson_type === 'html';
  useEffect(() => {
    if (!lessonId || !storedHtml) return undefined;
    let alive = true;
    setHtmlLoading(true);
    coursesAPI.lessonHtml(lessonId)
      .then(({ data }) => { if (alive) setHtml((prev) => (prev.dirty ? prev : { ...prev, content: data.content || '' })); })
      .catch(() => {})
      .finally(() => { if (alive) setHtmlLoading(false); });
    return () => { alive = false; };
  }, [lessonId, storedHtml]);

  const autofill = async () => {
    setProbing(true);
    try {
      const { data } = await coursesAPI.videoDuration(url);
      if (data.duration) {
        setDuration(data.duration);
        toast.success(`${t('Duration')}: ${data.duration}`);
      } else {
        toast.info(t('Could not detect the duration — enter it manually.'));
      }
    } catch {
      toast.info(t('Could not detect the duration — enter it manually.'));
    } finally {
      setProbing(false);
    }
  };

  const uploadFile = async (file, kind) => {
    setUploading(true);
    try {
      const { data } = await coursesAPI.upload(courseId, kind, file);
      if (kind === 'video') {
        setUrl(data.url);
        toast.success(t('Video uploaded'));
      } else {
        setAttachment({ path: data.url, type: data.type });
        toast.success(t('File uploaded'));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Upload failed'));
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      setTab('content');
      toast.error(t('Title is required'));
      return;
    }
    if (isHtml && !editing && !html.content && !htmlLoading) {
      setTab('content');
      toast.error(t('Upload or paste the HTML for this lesson'));
      return;
    }
    if (isHtml && html.dirty && utf8Len(html.content) > HTML_MAX) {
      toast.error(t('The HTML file is larger than 5 MB'));
      return;
    }
    setBusy(true);
    const payload = {
      title,
      lesson_type: type,
      video: { url: isUrlType ? url : '', duration: isUrlType ? duration : '' },
      attachment: isUrlType || isQuiz || isScorecard || isHtml ? { path: '', type: '' } : attachment,
      quiz: isQuiz ? { questions } : null,
      check: checkable ? { enabled: check.enabled, pass_pct: check.pass_pct, questions: check.questions } : null,
      summary,
    };
    if (isHtml) {
      payload.html = { source: html.source, filename: html.filename, height: html.height, fixed_px: html.fixed_px };
      if (html.dirty && html.content) payload.html.content = html.content;   // only ship the page when it changed
      // s137: an edited HTML lesson left without a page = remove the stored page
      else if (editing && !html.content && !htmlLoading) payload.html.clear = true;
    }
    try {
      if (editing) await coursesAPI.updateLesson(lesson.id, payload);
      else await coursesAPI.createLesson(sectionId, payload);
      onSaved();
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string' && /check/i.test(detail)) setTab('check');
      toast.error(detail || t('Save failed'));
    } finally {
      setBusy(false);
    }
  };

  const tabBtn = (key, label, extra) => (
    <button
      type="button"
      role="tab"
      aria-selected={tab === key}
      onClick={() => setTab(key)}
      className={`flex items-center gap-2 border-b-[3px] pb-3 pt-1 text-[14.5px] ${tab === key ? 'border-lms-ink font-bold text-lms-ink' : 'border-transparent font-semibold text-lms-sub hover:text-lms-ink'}`}
    >
      {label}{extra}
    </button>
  );

  return (
    <Modal title={editing ? t('Edit lesson') : t('New lesson')} onClose={onClose} wide="xl">
      <form onSubmit={save} className="space-y-5">
        {checkable && (
          <nav role="tablist" aria-label={t('Lesson editor')} className="-mt-1 flex gap-7 border-b border-lms-line">
            {tabBtn('content', t('Content'))}
            {tabBtn('check', t('Comprehension check'), check.enabled && checkCount > 0 ? (
              <span className="rounded-full bg-lms-tag-free-bg px-2 py-px text-xs font-bold text-lms-tag-free-ink lms-num">
                {checkCount} {checkCount === 1 ? t('question') : t('questions')}
              </span>
            ) : null)}
          </nav>
        )}

        <div className={tab === 'content' || !checkable ? 'space-y-4' : 'hidden'}>
          <div className="grid gap-4 sm:grid-cols-[1fr,14rem]">
            <Field label={t('Title')}>
              <input value={title} onChange={(e) => setTitle(e.target.value)} autoFocus className={inputCls} />
            </Field>
            <Field label={t('Lesson type')}>
              <select value={type} onChange={(e) => setType(e.target.value)} className={inputCls}>
                {LESSON_TYPES.map((x) => <option key={x.key} value={x.key}>{t(x.label)}</option>)}
              </select>
            </Field>
          </div>

          {isUrlType && (
            <>
              <Field label={type === 'video' ? t('Video URL (or upload a file below)') : t('URL')}>
                <input
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder={type === 'youtube' ? 'https://www.youtube.com/watch?v=…' : type === 'vimeo' ? 'https://vimeo.com/…' : 'https://…'}
                  className={inputCls}
                />
              </Field>
              {type === 'video' && (
                <label className={`${btnGhost} cursor-pointer`}>
                  <Clapperboard size={15} />
                  {uploading ? t('Uploading…') : t('Upload video file (mp4, 95 MB max)')}
                  <input type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" disabled={uploading}
                    onChange={(e) => e.target.files[0] && uploadFile(e.target.files[0], 'video')} />
                </label>
              )}
              {['video', 'youtube', 'vimeo'].includes(type) && (
                <div className="grid grid-cols-[1fr,auto] items-end gap-2">
                  <Field label={t('Duration (HH:MM:SS)')}>
                    <input value={duration} onChange={(e) => setDuration(e.target.value)} placeholder="00:00:00" className={inputCls} />
                  </Field>
                  {type !== 'video' && (
                    <button type="button" onClick={autofill} disabled={probing || !url} className={btnGhost}>
                      <Clock size={14} />
                      {probing ? t('Detecting…') : t('Autofill')}
                    </button>
                  )}
                </div>
              )}
            </>
          )}

          {isHtml && (
            <HtmlSource
              html={html}
              setHtml={setHtml}
              loading={htmlLoading}
              onRemove={editing ? () => setHtml({ ...html, content: '', filename: '', size: 0, dirty: false }) : null}
            />
          )}

          {isQuiz && (
            <Field label={t('Questions')}>
              <QuizBuilder questions={questions} onChange={setQuestions} />
            </Field>
          )}

          {isScorecard && (
            <div className={`${panelCls} p-4 text-sm text-lms-muted`}>
              <p className="mb-1 flex items-center gap-2 font-medium text-lms-text">
                <Gauge size={15} className="text-lms-accent" />
                {t('Wealth Baseline Scorecard')}
              </p>
              <p>{t('This lesson opens the Wealth Baseline Scorecard form. The member fills it in (name, email and location come from their profile) and is returned to the course automatically; the lesson completes once they submit. No configuration needed.')}</p>
            </div>
          )}

          {['document', 'image'].includes(type) && attachment.path && (
            <AttachmentPreview path={attachment.path} kind={type} />
          )}

          {['document', 'image'].includes(type) && (
            <Field label={type === 'document' ? t('Document file') : t('Image file')}>
              <div className="flex items-center gap-3">
                {attachment.path ? (
                  <span className="min-w-0 flex-1 truncate text-xs text-lms-muted">{attachment.path.split('/').pop()}</span>
                ) : (
                  <span className="flex-1 text-xs text-lms-faint">{t('No file yet')}</span>
                )}
                {attachment.path && (
                  <button type="button" onClick={() => setAttachment({ path: '', type: '' })} className="lms-press inline-flex h-9 shrink-0 items-center gap-1.5 rounded-lg px-3 text-[13px] font-bold text-lms-sale hover:bg-lms-raised">
                    <Eraser size={14} /> {t('Remove')}
                  </button>
                )}
                <label className={`${btnGhost} shrink-0 cursor-pointer`}>
                  {type === 'document' ? <FileText size={15} /> : <ImageIcon size={15} />}
                  {uploading ? t('Uploading…') : t('Upload')}
                  <input
                    type="file"
                    accept={type === 'document' ? '.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.zip' : 'image/*'}
                    className="hidden"
                    disabled={uploading}
                    onChange={(e) => e.target.files[0] && uploadFile(e.target.files[0], type)}
                  />
                </label>
              </div>
            </Field>
          )}

          <Field label={t('Summary (optional)')}>
            <textarea value={summary} onChange={(e) => setSummary(e.target.value)} rows={3} className={inputCls} />
          </Field>
        </div>

        {checkable && tab === 'check' && (
          <LessonCheckEditor check={check} onChange={setCheck} lessonId={lesson?.id} lessonTitle={title} />
        )}

        <div className="flex justify-end gap-2 border-t border-lms-line pt-4">
          <button type="button" onClick={onClose} className={btnGhost}>{t('Cancel')}</button>
          <button type="submit" disabled={busy || uploading} className={btnPrimary}>
            {busy ? t('Saving…') : t('Save lesson')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ------------------------------------------------------------- sortable rows
// s134 redesign: section = card with grey header (grip, fold chevron, title,
// lesson count, edit, ⋮); lesson = grip + coloured type chip + title +
// human-readable type pill + edit + ⋮ (Delete lives in the menu).

const TYPE_SHORT = {
  video: 'Video', youtube: 'YouTube', vimeo: 'Vimeo', url: 'Link', iframe: 'Embed',
  document: 'Document', image: 'Image', quiz: 'Quiz', wealth_scorecard: 'Scorecard', html: 'HTML',
};
const TYPE_TONE = {
  html: 'bg-lms-tag-free-bg text-lms-tag-free-ink',
  document: 'bg-lms-tag-free-bg text-lms-tag-free-ink',
  wealth_scorecard: 'bg-lms-tag-alt-bg text-lms-tag-alt-ink',
  quiz: 'bg-lms-tag-alt-bg text-lms-tag-alt-ink',
  video: 'bg-lms-current text-lms-ink',
  youtube: 'bg-lms-current text-lms-ink',
  vimeo: 'bg-lms-current text-lms-ink',
};
const tone = (type) => TYPE_TONE[type] || 'bg-lms-raised text-lms-body';

function SortableLesson({ lesson, onEdit, onDelete, onClear, previewHref }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: lesson.id });
  const Icon = (TYPE_META[lesson.lesson_type] || TYPE_META.video).icon;
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 10 : undefined }}
      className={`flex items-center gap-3 border-t border-lms-line bg-lms-surface py-2.5 pl-3.5 pr-3 ${isDragging ? 'shadow-[var(--lms-elev-2)]' : ''}`}
    >
      <button type="button" {...attributes} {...listeners} className="cursor-grab touch-none text-lms-faint hover:text-lms-ink" aria-label={t('Drag to reorder')}>
        <GripVertical size={15} />
      </button>
      <span className={`flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-lg ${tone(lesson.lesson_type)}`}>
        <Icon size={15} />
      </span>
      <button type="button" onClick={() => onEdit(lesson)} className="min-w-0 flex-1 truncate text-left text-sm text-lms-ink hover:text-lms-link">
        {lesson.title}
      </button>
      {lessonIsEmpty(lesson) && (
        <span className="rounded-full border border-dashed border-lms-pill px-2.5 py-0.5 text-[11.5px] font-bold text-lms-sub" title={t('This lesson has no content yet')}>
          {t('Empty')}
        </span>
      )}
      {lesson.video?.duration && <span className="text-xs text-lms-sub lms-num">{lesson.video.duration}</span>}
      {lesson.check?.enabled && lesson.check.questions?.length > 0 && (
        <span className="hidden items-center gap-1 text-xs font-semibold text-lms-sub md:inline-flex" title={t('Comprehension check')}>
          <ListChecks size={13} /> {lesson.check.questions.length}
        </span>
      )}
      <span className={`hidden rounded-full px-2.5 py-0.5 text-[11.5px] font-bold sm:inline ${tone(lesson.lesson_type)}`}>
        {t(TYPE_SHORT[lesson.lesson_type] || lesson.lesson_type)}
      </span>
      {previewHref && (
        <a href={previewHref} target="_blank" rel="noopener noreferrer" title={t('Preview this lesson as a student')} aria-label={t('Preview this lesson as a student')}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-lms-sub hover:bg-lms-raised hover:text-lms-ink">
          <Eye size={15} />
        </a>
      )}
      <IconButton label={t('Edit')} onClick={() => onEdit(lesson)}><Pencil size={14} /></IconButton>
      <RowMenu items={[
        { label: t('Clear content…'), icon: Eraser, onClick: () => onClear(lesson) },
        { label: t('Delete lesson'), icon: Trash2, onClick: () => onDelete(lesson), danger: true },
      ]} />
    </div>
  );
}

// s137: preview of the uploaded file inside the lesson editor — PDFs and
// images render inline; other formats (Word, Excel, PowerPoint, zip…) can't be
// shown by the browser, so they get an "Open file" link instead.
function AttachmentPreview({ path, kind }) {
  const [open, setOpen] = useState(true);
  const name = path.split('/').pop();
  const ext = (name.split('.').pop() || '').toLowerCase();
  const inline = kind === 'image' || ext === 'pdf';
  return (
    <div className="overflow-hidden rounded-xl border border-lms-line">
      <div className="flex items-center gap-2 border-b border-lms-line bg-lms-raised px-3.5 py-2">
        <Eye size={14} className="text-lms-sub" />
        <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-lms-ink">{t('Preview')} · {name}</span>
        <a href={path} target="_blank" rel="noopener noreferrer" className="text-[13px] font-bold text-lms-link hover:underline">{t('Open file')}</a>
        {inline && (
          <button type="button" onClick={() => setOpen(!open)} className="text-[13px] font-bold text-lms-sub hover:text-lms-ink">
            {open ? t('Hide') : t('Show')}
          </button>
        )}
      </div>
      {inline && open && (kind === 'image' ? (
        <img src={path} alt={name} className="max-h-[420px] w-full bg-lms-raised object-contain" />
      ) : (
        <iframe title={name} src={path} className="h-[480px] w-full bg-white" />
      ))}
      {!inline && (
        <p className="px-3.5 py-3 text-[13px] text-lms-sub">{t('This file type can’t be shown here — open it to check it.')}</p>
      )}
    </div>
  );
}

// s137: wipe a lesson's data but keep the lesson itself (title, type, place in
// the curriculum, students' progress). The admin picks what to clear.
function ClearLessonModal({ lesson, onClose, onCleared }) {
  const hasSummary = !!(lesson.summary && lesson.summary.trim());
  const hasCheck = !!(lesson.check && lesson.check.questions && lesson.check.questions.length);
  const hasContent = !lessonIsEmpty(lesson) && lesson.lesson_type !== 'wealth_scorecard';
  const [what, setWhat] = useState({ content: hasContent, summary: false, check: false });
  const [busy, setBusy] = useState(false);
  const any = what.content || what.summary || what.check;
  const row = (key, label, sub, enabled) => (
    <label className={`flex items-start gap-3 rounded-xl border px-4 py-3 ${enabled ? 'cursor-pointer border-lms-line hover:bg-lms-raised' : 'border-lms-line opacity-50'}`}>
      <input
        type="checkbox"
        checked={what[key]}
        disabled={!enabled}
        onChange={(e) => setWhat({ ...what, [key]: e.target.checked })}
        className="mt-0.5 h-4 w-4 accent-[var(--lms-sale)]"
      />
      <span className="flex flex-col gap-0.5">
        <span className="text-sm font-semibold text-lms-ink">{label}</span>
        <span className="text-[12.5px] text-lms-sub">{sub}</span>
      </span>
    </label>
  );
  const submit = async () => {
    setBusy(true);
    try {
      await coursesAPI.clearLesson(lesson.id, what);
      toast.success(t('Lesson content cleared'));
      onCleared();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Save failed'));
      setBusy(false);
    }
  };
  return (
    <Modal title={t('Clear lesson content')} onClose={onClose}>
      <div className="space-y-4">
        <p className="text-sm text-lms-body">
          <strong className="text-lms-ink">{lesson.title}</strong>{' '}
          {t('stays in the course with its title, type and position. Choose what to remove:')}
        </p>
        <div className="space-y-2">
          {row('content', t(CONTENT_LABEL[lesson.lesson_type] || 'The lesson material'),
            hasContent ? t('Students will see “No content yet” until you add new material.') : t('Nothing to remove.'), hasContent)}
          {row('summary', t('The lesson text (summary)'), hasSummary ? t('The written text shown under the lesson.') : t('Nothing to remove.'), hasSummary)}
          {row('check', t('The comprehension check'), hasCheck
            ? `${lesson.check.questions.length} ${lesson.check.questions.length === 1 ? t('question') : t('questions')} — ${t('past attempts are kept in the results.')}`
            : t('Nothing to remove.'), hasCheck)}
        </div>
        <p className="text-xs text-lms-sub">{t('This cannot be undone. Uploaded files are unlinked from the lesson, not deleted from the server.')}</p>
        <div className="flex justify-end gap-2 border-t border-lms-line pt-4">
          <button type="button" onClick={onClose} className={btnGhost}>{t('Cancel')}</button>
          <button
            type="button"
            onClick={submit}
            disabled={busy || !any}
            className="lms-press inline-flex h-11 items-center justify-center gap-2 rounded-[10px] bg-lms-sale px-5 text-sm font-bold text-white hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Eraser size={15} /> {busy ? t('Clearing…') : t('Clear selected')}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function SortableSection({ section, open, onToggle, children, onRename, onDelete, onAddLesson }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: section.id });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 10 : undefined }}
      className={`overflow-hidden rounded-xl border border-lms-line bg-lms-surface ${isDragging ? 'shadow-[var(--lms-elev-3)]' : ''}`}
    >
      <div className="flex items-center gap-3 bg-lms-raised py-3.5 pl-3.5 pr-3">
        <button type="button" {...attributes} {...listeners} className="cursor-grab touch-none text-lms-faint hover:text-lms-ink" aria-label={t('Drag to reorder')}>
          <GripVertical size={15} />
        </button>
        <button type="button" onClick={onToggle} aria-expanded={open} className="flex min-w-0 flex-1 items-center gap-3 text-left">
          <ChevronDown size={16} className={`shrink-0 text-lms-ink transition-transform ${open ? 'rotate-180' : ''}`} />
          <span className="min-w-0 flex-1 truncate text-[15px] font-bold text-lms-ink">{section.title}</span>
        </button>
        <span className="text-[13px] text-lms-sub lms-num">
          {section.lessons.length} {section.lessons.length === 1 ? t('lesson') : t('lessons')}
        </span>
        <IconButton label={t('Rename section')} onClick={() => onRename(section)}><Pencil size={14} /></IconButton>
        <RowMenu
          items={[
            { label: t('Add lesson'), icon: Plus, onClick: () => onAddLesson(section) },
            { label: t('Delete section'), icon: Trash2, onClick: () => onDelete(section), danger: true },
          ]}
        />
      </div>
      {open && (
        <>
          {children}
          <div className="border-t border-lms-line py-2.5 pl-12 pr-4">
            <button
              type="button"
              onClick={() => onAddLesson(section)}
              className="lms-press inline-flex h-9 items-center gap-1.5 rounded-lg border border-dashed border-lms-pill bg-lms-surface px-3 text-[13px] font-bold text-lms-ink hover:border-lms-ink"
            >
              <Plus size={14} /> {t('Add lesson')}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- main widget

export default function CurriculumBuilder({ courseId, courseSlug, onCountChange }) {
  const [sections, setSections] = useState(null);
  const [collapsed, setCollapsed] = useState({});
  const [lessonModal, setLessonModal] = useState(null); // {sectionId, lesson?}
  const [clearing, setClearing] = useState(null); // lesson being cleared (s137)
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const load = useCallback(async () => {
    try {
      const { data } = await coursesAPI.curriculum(courseId);
      setSections(data.sections);
      if (onCountChange) onCountChange((data.sections || []).reduce((n, s) => n + (s.lessons || []).length, 0));
    } catch {
      toast.error(t('Could not load the curriculum'));
    }
  }, [courseId, onCountChange]);
  useEffect(() => {
    load();
  }, [load]);

  const addSection = async () => {
    const title = window.prompt(t('Section title'));
    if (!title?.trim()) return;
    try {
      await coursesAPI.createSection(courseId, { title: title.trim() });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Save failed'));
    }
  };

  const renameSection = async (section) => {
    const title = window.prompt(t('Section title'), section.title);
    if (!title?.trim() || title.trim() === section.title) return;
    try {
      await coursesAPI.updateSection(section.id, { title: title.trim() });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Save failed'));
    }
  };

  const deleteSection = async (section) => {
    if (!window.confirm(`${t('Delete')} "${section.title}"? ${t('Its lessons are deleted too.')}`)) return;
    try {
      await coursesAPI.deleteSection(section.id);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Delete failed'));
    }
  };

  const deleteLesson = async (lesson) => {
    if (!window.confirm(`${t('Delete')} "${lesson.title}"?`)) return;
    try {
      await coursesAPI.deleteLesson(lesson.id);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Delete failed'));
    }
  };

  const onSectionDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const ids = sections.map((s) => s.id);
    const moved = arrayMove(ids, ids.indexOf(active.id), ids.indexOf(over.id));
    setSections((prev) => moved.map((id) => prev.find((s) => s.id === id)));
    try {
      await coursesAPI.reorderSections(courseId, moved);
    } catch {
      toast.error(t('Reorder failed'));
      load();
    }
  };

  const onLessonDragEnd = (section) => async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const ids = section.lessons.map((l) => l.id);
    const moved = arrayMove(ids, ids.indexOf(active.id), ids.indexOf(over.id));
    setSections((prev) => prev.map((s) =>
      s.id === section.id ? { ...s, lessons: moved.map((id) => s.lessons.find((l) => l.id === id)) } : s
    ));
    try {
      await coursesAPI.reorderLessons(section.id, moved);
    } catch {
      toast.error(t('Reorder failed'));
      load();
    }
  };

  if (sections === null) return <p className="text-sm text-lms-muted">{t('Loading…')}</p>;

  const lessonTotal = sections.reduce((n, s) => n + s.lessons.length, 0);
  const anyCollapsed = sections.some((s) => collapsed[s.id]);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="text-sm text-lms-body lms-num">
          <strong className="text-lms-ink">{sections.length} {sections.length === 1 ? t('section') : t('sections')}</strong>
          {' · '}{lessonTotal} {t('lessons')}{sections.length > 0 && ` · ${t('drag sections and lessons to reorder')}`}
        </span>
        <div className="flex items-center gap-2.5">
          {sections.length > 0 && (
            <button
              type="button"
              onClick={() => setCollapsed(anyCollapsed ? {} : Object.fromEntries(sections.map((s) => [s.id, true])))}
              className="h-11 px-3 text-sm font-bold text-lms-link hover:underline"
            >
              {anyCollapsed ? t('Expand all') : t('Collapse all')}
            </button>
          )}
          <button type="button" onClick={addSection} className={adminBtnPrimary}>
            <Plus size={16} strokeWidth={2.4} /> {t('New section')}
          </button>
        </div>
      </div>

      {sections.length === 0 ? (
        <EmptyState>{t('No sections yet — a course is sections of lessons.')}</EmptyState>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onSectionDragEnd}>
          <SortableContext items={sections.map((s) => s.id)} strategy={verticalListSortingStrategy}>
            <div className="flex max-w-[1040px] flex-col gap-3.5">
              {sections.map((section) => (
                <SortableSection
                  key={section.id}
                  section={section}
                  open={!collapsed[section.id]}
                  onToggle={() => setCollapsed((prev) => ({ ...prev, [section.id]: !prev[section.id] }))}
                  onRename={renameSection}
                  onDelete={deleteSection}
                  onAddLesson={(s) => setLessonModal({ sectionId: s.id })}
                >
                  {section.lessons.length === 0 ? (
                    <p className="border-t border-lms-line px-5 py-3 text-[13px] text-lms-sub">{t('No lessons yet.')}</p>
                  ) : (
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onLessonDragEnd(section)}>
                      <SortableContext items={section.lessons.map((l) => l.id)} strategy={verticalListSortingStrategy}>
                        {section.lessons.map((lesson) => (
                          <SortableLesson
                            key={lesson.id}
                            lesson={lesson}
                            onEdit={(l) => setLessonModal({ sectionId: section.id, lesson: l })}
                            onDelete={deleteLesson}
                            onClear={setClearing}
                            previewHref={courseSlug ? `/learn/${courseSlug}?lesson=${lesson.id}` : null}
                          />
                        ))}
                      </SortableContext>
                    </DndContext>
                  )}
                </SortableSection>
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {clearing && <ClearLessonModal lesson={clearing} onClose={() => setClearing(null)} onCleared={load} />}

      {lessonModal && (
        <LessonModal
          courseId={courseId}
          sectionId={lessonModal.sectionId}
          lesson={lessonModal.lesson}
          onClose={() => setLessonModal(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}
